/**
 * Audit-event server→display mappers (ADR-003 + ADR-005).
 *
 * Extracted from ``useAuditEvents.ts`` so the admin drilldown
 * (#990 — ``useAdminAuditEvents``) can render the same
 * ``AuditLogEntry`` rows without re-implementing event-type
 * bucketing, action narrowing, or entity/user fallback rules.
 *
 * The mappers are pure: server shape (``ServerAuditEvent``) in,
 * design-system shape (``AuditLogEntry``) out. No React, no
 * query-cache, no scope coupling.
 */
import type { AuditActionType, AuditLogEntry } from '@hofsync/design-system';

export interface ServerAuditEvent {
  event_id: string;
  occurred_at: string;
  event_type: string;
  action: string;
  actor_type: string;
  actor_id: string | null;
  actor_label: string | null;
  object_type: string | null;
  object_id: string | null;
  object_label: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface ServerAuditEventsResponse {
  events: ServerAuditEvent[];
  page: { next_cursor: string | null; prev_cursor?: string | null; limit: number };
}

const KNOWN_ACTIONS: ReadonlySet<AuditActionType> = new Set<AuditActionType>([
  'upload',
  'download',
  'edit',
  'delete',
  'share',
  'login',
  'logout',
  'create',
  'update',
  'submit',
  'sync',
  'backup',
  'notification',
]);

export function coerceAction(action: string): AuditActionType {
  // Server is the source of truth for `action`; we narrow into the
  // design-system enum only at the display boundary so an unexpected new
  // value (rolled out server-side first) renders as `update` rather than
  // throwing in the table.
  return KNOWN_ACTIONS.has(action as AuditActionType)
    ? (action as AuditActionType)
    : 'update';
}

// Mirror of `backend/src/services/audit/event_type_mapper.py:_LENS_TREE_MAP`.
// `tenant.*` is split: profile / settings / invites are user-activity,
// integrations are system. So we match exact event_types for the tenant
// family before falling through to prefix-based bucketing.
const EXACT_EVENT_TYPE_TO_CATEGORY: ReadonlyMap<string, string> = new Map([
  // tenant.* — split per backend mapper
  ['tenant.profile_updated', 'Benutzeraktivität'],
  ['tenant.settings_changed', 'Benutzeraktivität'],
  ['tenant.feature_flag_changed', 'Benutzeraktivität'],
  ['tenant.invite_sent', 'Benutzeraktivität'],
  ['tenant.invite_accepted', 'Benutzeraktivität'],
  ['tenant.invite_revoked', 'Benutzeraktivität'],
  ['tenant.integration_connected', 'System'],
  ['tenant.integration_disconnected', 'System'],
  // twin.event.* lives under System/Notifications per the lens tree,
  // not Betriebsdaten (which is twin.entity.*).
  ['twin.event.delivery_overdue', 'System'],
  ['twin.event.auto_resolved', 'System'],
]);

const EVENT_TYPE_PREFIX_TO_CATEGORY: ReadonlyArray<[RegExp, string]> = [
  [/^document\./, 'Dokumente'],
  [/^email\./, 'Dokumente'],
  [/^user_correction\./, 'Dokumente'],
  [/^twin\.entity\./, 'Betriebsdaten'],
  [/^twin\.event\./, 'System'],
  [/^auth\./, 'Benutzeraktivität'],
  [/^sync\./, 'System'],
  [/^compliance\./, 'Compliance'],
  [/^system\./, 'System'],
];

export function eventTypeToCategoryLabel(eventType: string): string {
  const exact = EXACT_EVENT_TYPE_TO_CATEGORY.get(eventType);
  if (exact) return exact;
  for (const [pattern, label] of EVENT_TYPE_PREFIX_TO_CATEGORY) {
    if (pattern.test(eventType)) return label;
  }
  return 'System';
}

function describe(event: ServerAuditEvent): string {
  const explanation = event.metadata?.['explanation'];
  if (typeof explanation === 'string' && explanation.length > 0) {
    return explanation;
  }
  // Fallback: humanise the event_type tail (e.g. "document.uploaded" → "uploaded").
  const tail = event.event_type.split('.').slice(1).join(' ').replace(/_/g, ' ');
  return tail || event.event_type;
}

function mapEntity(event: ServerAuditEvent): AuditLogEntry['entity'] {
  if (!event.object_type || !event.object_id) return undefined;
  const unresolved = event.object_label == null;
  return {
    type: event.object_type,
    name: event.object_label ?? event.object_id,
    ...(unresolved ? { unresolved: true } : {}),
  };
}

function mapUser(event: ServerAuditEvent): AuditLogEntry['user'] {
  const fallbackId = event.actor_id ?? event.actor_type;
  if (event.actor_label == null) {
    return { name: fallbackId, unresolved: true };
  }
  return { name: event.actor_label };
}

export function mapServerEventToEntry(event: ServerAuditEvent): AuditLogEntry {
  return {
    id: event.event_id,
    timestamp: event.occurred_at,
    action: coerceAction(event.action),
    event_type: event.event_type,
    category: eventTypeToCategoryLabel(event.event_type),
    user: mapUser(event),
    description: describe(event),
    entity: mapEntity(event),
  };
}
